<template>
  <Wrapper class="q-px-lg q-py-xl">
    <h5 class="q-mb-xl">Payment</h5>

    <section class="filter q-mb-md bg-gray flex justify-between">
      <div class="container-fluid overflow-auto">
        <div class="row items-center q-pa-sm q-col-gutter-md">
          <div class="col-lg-4">
            <q-select
              v-model="type"
              dense
              label="Type"
              hint="Charge type of payment"
              emit-value
              map-options
              :options="options"
            />
          </div>

          <div class="col-lg-4">
            <q-input v-model="search" dense label="Search" hint="Search by transporter (ID, company name) or UID" />
          </div>

          <div class="col-lg-2">
            <q-btn
              flat
              color="primary"
              icon="cached"
              label="reset"
              class="full-width"
              :disable="!type && !search"
              @click="reset"
            />
          </div>

          <div class="col-lg-2">
            <q-btn
              color="primary"
              icon="search"
              label="Search"
              class="full-width"
              :disable="!type && !search"
              @click="debouncedFetchData"
            />
          </div>
        </div>
      </div>
    </section>

    <DocExport />

    <section class="q-mb-md">
      <DataTable ref="table" :columns="columns">
        <template v-if="isLoading">
          <tr v-for="n in 10" :key="n">
            <td v-for="m in columns" :key="m">
              <q-skeleton type="text" width="7rem" />
            </td>
          </tr>
        </template>

        <tr v-for="payment in payments" v-else :key="payment.id">
          <td class="text-primary">{{ payment.id | formatId }}</td>
          <td>{{ payment.createdAt | formatDateTime }}</td>
          <td>
            <span v-if="payment.datePaid">{{ payment.datePaid | formatDateTime }}</span>
          </td>
          <td>{{ payment.stripeInvoiceId }}</td>
          <td class="text-uppercase">{{ payment.company.tradingName }}</td>
          <td>{{ payment.company.id | formatId }}</td>
          <td class="text-capitalize">{{ payment.type }}</td>
          <td>{{ payment.amount | formatCurrency }}</td>
          <td>{{ payment.gst | formatCurrency }}</td>
          <td>{{ payment.totalPrice | formatCurrency }}</td>
          <td class="text-center">
            <q-icon v-if="payment.paid" name="check_circle" color="positive" size="sm" />
          </td>
        </tr>
      </DataTable>
    </section>

    <Pagination v-if="totalPages > 1" :page.sync="currentPage" :total="totalPages" />
  </Wrapper>
</template>

<script>
import debounce from 'lodash/debounce';
import { stringify, parse } from 'query-string';
import Wrapper from '@/components/Wrapper.vue';
import Pagination from '@/components/Pagination.vue';
import DataTable from '@/components/DataTableNew.vue';
import formatDateMixin from '@/utils/mixins/formatDateMixin';
import DocExport from '../../components/DocExport.vue';
import { getErrorMessage } from '../../utils';

export default {
  name: 'AdminPayment',

  components: {
    Wrapper,
    DocExport,
    Pagination,
    DataTable,
  },

  mixins: [formatDateMixin],

  data() {
    return {
      isLoading: false,
      currentPage: 1,
      totalPages: 1,
      type: '',
      search: '',
      payments: [],
    };
  },

  computed: {
    columns() {
      return [
        'Payment ID',
        'Date',
        'Due Date',
        'UID Invoice',
        'Transport Company',
        'Transporter ID',
        'Charge Type',
        'Subtotal',
        'GST',
        'Total',
        'Payment State',
      ];
    },
    options() {
      return [
        { label: 'Commission', value: 'commission' },
        { label: 'Subscription', value: 'subscription' },
      ];
    },
  },

  watch: {
    currentPage() {
      this.debouncedFetchData();

      window.scrollTo({ behavior: 'smooth', top: this.$refs.table.$el.offsetTop });
    },
  },

  async created() {
    this.debouncedFetchData = debounce(this.fetchData, 500);
    this.fetchData();
  },

  methods: {
    async fetchData() {
      this.isLoading = true;

      try {
        const qs = stringify(
          { search: this.search.trim(), type: this.type, page: this.currentPage },
          { skipEmptyString: true }
        );
        const {
          data: { data, pagination },
        } = await this.$axios.get('/admin/payments', { params: parse(qs) });

        this.payments = data;
        this.currentPage = pagination.page;
        this.totalPages = pagination.pages;
      } catch (error) {
        this.$q.notify({
          message: getErrorMessage(error),
          type: 'negative',
          actions: [{ label: 'Try again', color: 'yellow', handlers: this.fetchData }],
        });
      } finally {
        this.isLoading = false;
      }
    },
    debouncedFetchData() {},
    reset() {
      this.type = '';
      this.search = '';
      this.debouncedFetchData();
    },
  },
};
</script>
